/**
 * Canonical money-unit conversion helpers (frontend copy of the API's money.ts).
 *
 * Cost fields and salePrice are stored/transmitted as integer MINOR units
 * (e.g. 89997 = 899.97). The UI and spreadsheet exports show human-facing MAJOR
 * units (two decimals). Convert ONLY at the boundary:
 *   - display / export:  fromMinorUnits(minor) → major
 *   - input / import:    toMinorUnits(major)   → minor
 * Never do inline `x / 100` or `Math.round(x * 100)` in components — use these,
 * which are robust against IEEE-754 scaling error (e.g. 899.97 * 100 ===
 * 89996.99999999999, 2.675 * 100 === 267.49999999999997).
 */

/** Convert a major-unit amount (899.97) to integer minor units (89997). */
export function toMinorUnits(major: number): number {
  if (typeof major !== 'number' || !Number.isFinite(major)) {
    throw new TypeError(`toMinorUnits: expected a finite number, received ${String(major)}`);
  }
  return Math.round(Number((major * 100).toFixed(2)));
}

/** Null/undefined-tolerant {@link toMinorUnits} for optional form fields. */
export function toMinorUnitsOrNull(major: number | null | undefined): number | null {
  return major == null ? null : toMinorUnits(major);
}

/** Convert integer minor units (89997) back to a major decimal (899.97). */
export function fromMinorUnits(minor: number): number {
  if (typeof minor !== 'number' || !Number.isFinite(minor)) {
    throw new TypeError(`fromMinorUnits: expected a finite number, received ${String(minor)}`);
  }
  return minor / 100;
}

/** Null/undefined-tolerant {@link fromMinorUnits} for optional display values. */
export function fromMinorUnitsOrNull(minor: number | null | undefined): number | null {
  return minor == null ? null : fromMinorUnits(minor);
}

/**
 * Single rounding rule for derived money in minor units: a per-unit minor cost
 * × a (possibly fractional) quantity, rounded to the nearest whole minor unit.
 */
export function costTimesQty(unitCostMinor: number, qty: number): number {
  if (typeof unitCostMinor !== 'number' || !Number.isFinite(unitCostMinor)) {
    throw new TypeError(`costTimesQty: unitCostMinor must be a finite number, received ${String(unitCostMinor)}`);
  }
  if (typeof qty !== 'number' || !Number.isFinite(qty)) {
    throw new TypeError(`costTimesQty: qty must be a finite number, received ${String(qty)}`);
  }
  return Math.round(unitCostMinor * qty);
}
